
import { Component } from '@theme/component';
import {
  ThemeEvents,
  ZoomMediaSelectedEvent,
  SlideshowSelectEvent,
} from '@theme/events';
import { StandardEvents } from '@shopify/events';

const sameId = (a, b) =>
  a != null && b != null && String(a) === String(b);

const isColor = (name) =>
  ['color', 'colour', 'màu', 'màu sắc'].includes(
    String(name || '').trim().toLowerCase()
  );

export class MediaGallery extends Component {
  #controller = new AbortController();
  #requestId = 0;

  #frame = 0;
  #initFrame = 0;
  #initAttempts = 0;
  #initTimer = 0;

  #map = [];
  #expectedMediaId = null;

  #automaticMediaId = null;
  #automaticClearTimer = 0;

  #fromGallery = false;
  #userInteracted = false;

  #handleSlideshowReady = () => {
    if (!this.isConnected || this.#userInteracted) {
      return;
    }

    this.#initialize();
  };

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    this.#userInteracted = false;

    this.#loadMap();

    const signal = this.#controller.signal;
    const section = this.closest('.shopify-section, dialog');

    section?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    section?.addEventListener(
      'change',
      this.#handleOptionChange,
      { signal, capture: true }
    );

    this.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    window.addEventListener(
      'pageshow',
      this.#handlePageShow,
      { signal }
    );

    this.slideshow?.addEventListener(
      'slideshow:ready',
      this.#handleSlideshowReady,
      { signal }
    );

    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#requestId++;

    this.#stopInitialization();

    cancelAnimationFrame(this.#frame);
    this.#frame = 0;

    this.#clearAutomatic();

    this.#expectedMediaId = null;
  }

  #loadMap() {
    try {
      const data = JSON.parse(
        this.dataset.variantMediaMap || '[]'
      );

      this.#map = Array.isArray(data) ? data : [];
    } catch {
      this.#map = [];
    }
  }

  get #picker() {
    const section = this.closest('.shopify-section');

    return section?.querySelector('variant-picker') || null;
  }

  get #colorIndex() {
    const raw = this.dataset.colorOptionIndex;

    if (raw == null || raw === '') return -1;

    const index = Number(raw);

    return Number.isInteger(index) && index >= 0
      ? index
      : -1;
  }

  get #selectedColor() {
    const picker = this.#picker;

    const selected = picker
      ?.getAllSelectedOptions?.()
      ?.find(option => isColor(option.name));

    return selected?.value || null;
  }

  #mediaForColor(color) {
    if (!color || this.#colorIndex < 0) {
      return null;
    }

    const item = this.#map.find(
      item =>
        item.mediaId != null &&
        item.options?.[this.#colorIndex] === color
    );

    return item?.mediaId ?? null;
  }

  #colorForMedia(mediaId) {
    if (mediaId == null || this.#colorIndex < 0) {
      return null;
    }

    const colors = new Set();

    for (const item of this.#map) {
      if (!sameId(item.mediaId, mediaId)) {
        continue;
      }

      const color = item.options?.[this.#colorIndex];

      if (color) {
        colors.add(color);
      }
    }

    return colors.size === 1
      ? [...colors][0]
      : null;
  }

  #slides() {
    return Array.from(this.slideshow?.slides || []);
  }

  #currentMediaId() {
    const slider = this.slideshow;

    if (!slider) return null;

    const slides = this.#slides();

    const current = slides[slider.current];

    if (current?.dataset.mediaId) {
      return current.dataset.mediaId;
    }

    const visible = slides.find(
      slide => slide.getAttribute('aria-hidden') === 'false'
    );

    return visible?.dataset.mediaId || null;
  }

  #mediaIndex(mediaId) {
    return this.#slides().findIndex(
      slide => sameId(slide.dataset.mediaId, mediaId)
    );
  }

  #clearAutomatic() {
    clearTimeout(this.#automaticClearTimer);

    this.#automaticClearTimer = 0;
    this.#automaticMediaId = null;
  }

  #stopInitialization() {
    cancelAnimationFrame(this.#initFrame);
    clearTimeout(this.#initTimer);

    this.#initFrame = 0;
    this.#initTimer = 0;
  }

  
  #selectMedia(mediaId) {
    const slider = this.slideshow;

    if (!slider || mediaId == null) {
      return false;
    }

    const index = this.#mediaIndex(mediaId);

    if (index < 0) {
      return false;
    }

    this.#clearAutomatic();
    this.#automaticMediaId = String(mediaId);

    // A short guard for scroll events caused by select().
    this.#automaticClearTimer = setTimeout(() => {
      this.#clearAutomatic();
    }, 250);

    try {
      Promise.resolve(
        slider.select(index, undefined, {
          animate: false,
        })
      ).catch(error => {
        if (error?.name !== 'AbortError') {
          console.warn('[media-gallery]', error);
        }
      });

      return true;
    } catch (error) {
      this.#clearAutomatic();
      console.warn('[media-gallery]', error);
      return false;
    }
  }

  #scheduleMedia(mediaId) {
    if (mediaId == null) return;

    this.#expectedMediaId = String(mediaId);

    cancelAnimationFrame(this.#frame);

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;

      if (!this.isConnected) return;

      const target = this.#expectedMediaId;
      this.#expectedMediaId = null;

      if (target == null) return;

      this.#selectMedia(target);
    });
  }

  #syncSelectedColor() {
    const color = this.#selectedColor;

    const mediaId = this.#mediaForColor(color);

    if (mediaId != null) {
      this.#scheduleMedia(mediaId);
    }
  }

  #initialize() {
    this.#stopInitialization();

    if (!this.isConnected || this.#userInteracted) {
      return;
    }

    const slider = this.slideshow;
    const slides = slider?.slides;

    if (!slides?.length) return;

    const color = this.#selectedColor;
    const mediaId = this.#mediaForColor(color);

    const index =
      mediaId != null
        ? this.#mediaIndex(mediaId)
        : -1;

    const targetIndex = index >= 0 ? index : 0;

    // Before the slideshow is ready, Liquid's initial-slide
    // remains responsible for the initial image.
    if (!slider.hasAttribute('data-media-ready')) {
      return;
    }

    slider.select(targetIndex, undefined, {
      animate: false,
    });
  }

  #handlePageShow = event => {
    if (!this.isConnected) return;

    if (event.persisted) {
      this.#userInteracted = false;
      this.#initialize();
    }
  };

  #handleOptionChange = event => {
    const picker = this.#picker;
    const target = event.target;

    if (
      !picker ||
      !(target instanceof Element) ||
      !picker.contains(target) ||
      this.#fromGallery
    ) {
      return;
    }

    const selectedOption =
      target instanceof HTMLSelectElement
        ? target.selectedOptions[0]
        : target;

    if (!isColor(selectedOption?.dataset.optionName)) {
      return;
    }

    this.#userInteracted = true;
    this.#stopInitialization();

    queueMicrotask(() => {
      if (this.isConnected) {
        this.#syncSelectedColor();
      }
    });
  };

  #handleSlideSelect = event => {
    if (event.target !== this.slideshow) {
      return;
    }

    const detail = event.detail;

    if (!detail?.userInitiated) {
      return;
    }

    const mediaId = detail.slide?.dataset.mediaId;

    if (!mediaId) return;

    // Ignore scroll events caused by programmatic
    // slideshow selection.
    
    if (
      detail.trigger === 'scroll' &&
      this.#automaticMediaId !== null &&
      sameId(mediaId, this.#automaticMediaId)
    ) {
      this.#clearAutomatic();
      return;
    }

    this.#userInteracted = true;
    this.#stopInitialization();
    this.#clearAutomatic();

    cancelAnimationFrame(this.#frame);

    this.#frame = 0;
    this.#expectedMediaId = null;

    const color = this.#colorForMedia(mediaId);

    if (!color || color === this.#selectedColor) {
      return;
    }

    const picker = this.#picker;

    if (!picker) return;

    const radio = [
      ...picker.querySelectorAll(
        'input[type="radio"][data-option-name]'
      ),
    ].find(
      input =>
        isColor(input.dataset.optionName) &&
        input.value === color &&
        !input.disabled
    );

    const select = [
      ...picker.querySelectorAll('select'),
    ].find(element =>
      [...element.options].some(
        option =>
          isColor(option.dataset.optionName) &&
          option.value === color &&
          !option.disabled
      )
    );

    if (!radio && !select) {
      return;
    }

    this.#fromGallery = true;

    try {
      if (radio) {
        radio.checked = true;

        radio.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );
      } else {
        select.value = color;

        select.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );
      }
    } finally {
      this.#fromGallery = false;
    }
  };

  #handleProductSelect = event => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card') ||
      event.target !== this.#picker ||
      !event.promise?.then
    ) {
      return;
    }

    const requestId = ++this.#requestId;

    event.promise
      .then(({ detail }) => {
        if (
          !this.isConnected ||
          requestId !== this.#requestId ||
          !detail?.html
        ) {
          return;
        }

        const incoming =
          detail.html.querySelector('media-gallery');

        if (!incoming) return;

        const getMediaIds = gallery =>
          [
            ...gallery.querySelectorAll(
              'slideshow-slide[data-media-id]'
            ),
          ].map(slide => slide.dataset.mediaId);

        const oldIds = getMediaIds(this);
        const newIds = getMediaIds(incoming);

        const sameGallery =
          oldIds.length > 0 &&
          oldIds.length === newIds.length &&
          oldIds.every(
            (id, index) => id === newIds[index]
          );

        if (!sameGallery) {
          this.replaceWith(incoming);
          return;
        }

        this.dataset.variantMediaMap =
          incoming.dataset.variantMediaMap || '[]';

        this.dataset.colorOptionIndex =
          incoming.dataset.colorOptionIndex ?? '-1';

        this.dataset.selectedMediaId =
          incoming.dataset.selectedMediaId || '';

        this.#loadMap();

        this.#syncSelectedColor();
      })
      .catch(error => {
        if (
          requestId === this.#requestId &&
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[media-gallery] Product update failed:',
            error
          );
        }
      });
  };

  #handleZoomMediaSelected = event => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent
      ?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    MediaGallery
  );
}
